/**
 * Adds copy buttons to code blocks.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

export class CopyButtonEnricher extends BaseEnricher {
  constructor(config: EnricherConfig = {}) {
    super({ priority: 80, ...config });
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    container.querySelectorAll('pre').forEach(pre => {
      const code = pre.querySelector('code');
      if (!code) return;

      // Skip if already has copy button
      if (pre.querySelector('.code-copy-btn')) return;

      const copyBtn = this.createCopyButton();
      this.attachCopyHandler(copyBtn, code as HTMLElement);
      pre.appendChild(copyBtn);
    });
  }

  /**
   * Create a copy button element.
   */
  private createCopyButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'code-copy-btn';
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
      </svg>
      Copy
    `;
    return btn;
  }

  /**
   * Attach click handler to copy button.
   */
  private attachCopyHandler(btn: HTMLButtonElement, code: HTMLElement): void {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Strip trailing newline that marked.js appends to fenced code blocks.
      // Pasting it into a terminal (PowerShell especially) submits the line.
      const text = (code.textContent ?? '').replace(/\n+$/, '');
      try {
        await navigator.clipboard.writeText(text);
        this.showCopiedFeedback(btn);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    });
  }

  /**
   * Show visual feedback when content is copied.
   */
  private showCopiedFeedback(btn: HTMLButtonElement): void {
    btn.classList.add('copied');
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
      </svg>
      Copied!
    `;

    setTimeout(() => {
      btn.classList.remove('copied');
      btn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
        </svg>
        Copy
      `;
    }, 2000);
  }
}
